type AdminUser = {
  id: string;
  username?: string;
  email: string;
  phone: string;
  display_name?: string;
  role: string;
  status: string;
  plan_id: string;
  wallet?: { daily?: number; daily_limit?: number; purchased?: number; low_credit?: boolean };
};

type ApiProvider = {
  id: string;
  name: string;
  base_url: string;
  image_model: string;
  api_mode: "images" | "responses";
  images_concurrency: number;
  api_key?: string;
  api_key_set?: boolean;
  api_key_masked?: string;
};

type ApiSettings = {
  active_provider_id: string;
  codex_mode: "images" | "responses";
  providers: ApiProvider[];
};

const $ = <T extends HTMLElement = HTMLElement>(selector: string): T | null => document.querySelector<T>(selector);
const escapeHtml = (value: unknown): string => String(value ?? "").replace(/[&<>"']/g, (char) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#039;" })[char] || char);
const credit = (value: unknown): string => Number(value || 0).toLocaleString("vi-VN");
const money = (value: unknown): string => `${Number(value || 0).toLocaleString("vi-VN")}đ`;
const DEFAULT_API_BASE_URL = "https://api.openai.com/v1";
const DEFAULT_GATEWAY_BASE_URL = "https://api.hvdai.shop/v1";
const DEFAULT_IMAGE_MODEL = "gpt-image-2";
const summaryLabels: Record<string, string> = {
  users: "Thành viên",
  active_users: "Đang hoạt động",
  admins: "Admin",
  credits_purchased: "Credit đã nạp",
  credits_used: "Credit đã dùng",
  revenue_vnd: "Doanh thu",
  paid_orders: "Đơn đã trả",
  affiliates: "Affiliate",
};
const renderLevels = [
  { id: "low", label: "Nhanh" },
  { id: "medium", label: "Cân bằng" },
  { id: "high", label: "Cao" },
];
const sizeLevels = [
  { id: "standard", label: "1K" },
  { id: "2k", label: "2K" },
  { id: "4k", label: "4K" },
];

const state: { user: AdminUser | null; tab: string; apiSettings: ApiSettings | null } = { user: null, tab: "users", apiSettings: null };

async function api<T>(url: string, init?: RequestInit): Promise<T> {
  const response = await fetch(url, { credentials: "same-origin", ...init });
  const data = await response.json().catch(() => ({}));
  if (!response.ok) throw new Error(data.detail || "Không thể thực hiện thao tác");
  return data as T;
}

function setStatus(message: string, type = ""): void {
  const status = $("#adminPageStatus");
  if (!status) return;
  status.textContent = message;
  status.className = `admin-page-status ${type}`.trim();
}

function setAuthMode(isAuthed: boolean): void {
  $("#adminLoginPanel")?.classList.toggle("hidden", isAuthed);
  $("#adminDashboard")?.classList.toggle("hidden", !isAuthed);
  const identity = $("#adminIdentity");
  if (identity && state.user) identity.textContent = state.user.display_name || state.user.username || state.user.email;
}

async function refreshSession(): Promise<void> {
  try {
    const data = await api<{ user: AdminUser }>("/api/account/me");
    if (data.user.role !== "admin") {
      state.user = null;
      setAuthMode(false);
      setStatus("Tài khoản này không có quyền quản trị", "error");
      return;
    }
    state.user = data.user;
    setAuthMode(true);
    await loadAdminTab(state.tab);
  } catch {
    state.user = null;
    setAuthMode(false);
  }
}

async function login(event: Event): Promise<void> {
  event.preventDefault();
  const form = new FormData(event.currentTarget as HTMLFormElement);
  try {
    const data = await api<{ user: AdminUser }>("/api/account/login", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ identifier: form.get("identifier"), password: form.get("password") }),
    });
    if (data.user.role !== "admin") {
      setStatus("Tài khoản này không có quyền quản trị", "error");
      return;
    }
    state.user = data.user;
    setStatus("");
    setAuthMode(true);
    await loadAdminTab("users");
  } catch (error) {
    setStatus(error instanceof Error ? error.message : "Đăng nhập thất bại", "error");
  }
}

async function logout(): Promise<void> {
  await api("/api/account/logout", { method: "POST" });
  state.user = null;
  setAuthMode(false);
  setStatus("Đã đăng xuất", "ok");
}

async function loadAdminTab(tab: string): Promise<void> {
  state.tab = tab;
  document.querySelectorAll<HTMLElement>(".admin-tab").forEach((item) => item.classList.toggle("active", item.dataset.adminTab === tab));
  try {
    const summary = await api<{ summary: Record<string, number> }>("/api/admin/summary");
    const summaryGrid = $("#adminSummaryGrid");
    if (summaryGrid) summaryGrid.innerHTML = Object.entries(summary.summary).slice(0, 8).map(([key, value]) => `<div><span>${escapeHtml(summaryLabels[key] || key)}</span><strong>${key.includes("vnd") ? money(value) : credit(value)}</strong></div>`).join("");
    if (tab === "users") await renderUsers();
    if (tab === "plans") await renderPlans();
    if (tab === "packages") await renderPackages();
    if (tab === "pricing") await renderPricing();
    if (tab === "api") await renderApiRouting();
    if (tab === "orders") await renderOrders();
    if (tab === "affiliates") await renderAffiliates();
    if (tab === "withdrawals") await renderWithdrawals();
    if (tab === "integrations") await renderIntegrations();
    if (tab === "audit") await renderAudit();
  } catch (error) {
    setStatus(error instanceof Error ? error.message : "Không tải được dữ liệu quản trị", "error");
  }
}

function panel(): HTMLElement | null {
  return $("#adminPanel");
}

function costMatrixMarkup(costs: any): string {
  return sizeLevels.map((size) => {
    const row = costs?.[size.id] || {};
    return `<p>${size.label}: ${renderLevels.map((level) => `${level.label} <strong>${credit(row[level.id])}</strong>`).join(" · ")}</p>`;
  }).join("");
}

function packageEstimateSummary(item: any): string {
  const total = Number(item.total_credits || item.credits || 0);
  const estimates = item.image_estimates || {};
  const fast1k = estimates.fast_1k ?? estimates.low ?? Math.floor(total / 3);
  const balanced1k = estimates.balanced_1k ?? estimates.medium ?? Math.floor(total / 5);
  const high4k = estimates.high_4k ?? Math.floor(total / 50);
  return `1K Nhanh ${credit(fast1k)}<small>1K Cân bằng ${credit(balanced1k)} · 4K Cao ${credit(high4k)}</small>`;
}

async function renderUsers(): Promise<void> {
  const [data, planData] = await Promise.all([api<{ users: AdminUser[] }>("/api/admin/users"), api<{ plans: any[] }>("/api/admin/plans")]);
  const planOptions = planData.plans.map((plan) => `<option value="${escapeHtml(plan.id)}">${escapeHtml(plan.name)}</option>`).join("");
  const target = panel();
  if (!target) return;
  target.innerHTML = `<div id="adminUserDetail" class="admin-detail-strip"></div><div class="admin-table-wrap"><table class="admin-table"><thead><tr><th>Thành viên</th><th>Gói</th><th>Credit hôm nay</th><th>Credit mua</th><th>Trạng thái</th><th>Thao tác</th></tr></thead><tbody>${data.users.map((user) => `<tr><td>${escapeHtml(user.username || user.email)}<small>${escapeHtml(user.email)} · ${escapeHtml(user.phone)}</small></td><td><select class="control admin-user-plan" data-user-id="${escapeHtml(user.id)}">${planOptions}</select></td><td>${credit(user.wallet?.daily)} / ${credit(user.wallet?.daily_limit || 100)}${user.wallet?.low_credit ? `<small class="admin-low-credit">Sắp hết credit</small>` : ""}</td><td>${credit(user.wallet?.purchased)}<div class="admin-credit-adjust"><input class="control" type="number" min="1" placeholder="+credit" data-credit-input="${escapeHtml(user.id)}"><button class="ghost-button" type="button" data-credit-button="${escapeHtml(user.id)}">Cộng</button></div></td><td><select class="control admin-user-status" data-user-id="${escapeHtml(user.id)}"><option value="active" ${user.status === "active" ? "selected" : ""}>Hoạt động</option><option value="suspended" ${user.status === "suspended" ? "selected" : ""}>Đã khóa</option></select></td><td><div class="admin-row-actions"><button class="ghost-button" type="button" data-detail-user="${escapeHtml(user.id)}">Chi tiết</button><button class="ghost-button" type="button" data-reset-password-user="${escapeHtml(user.id)}" data-reset-password-email="${escapeHtml(user.email)}" title="Đặt lại mật khẩu về 12345678">Reset MK</button><button class="ghost-button" type="button" data-save-user="${escapeHtml(user.id)}">Lưu</button></div></td></tr>`).join("")}</tbody></table></div>`;
  target.querySelectorAll<HTMLSelectElement>(".admin-user-plan").forEach((element) => { element.value = data.users.find((user) => user.id === element.dataset.userId)?.plan_id || "free"; });
  target.querySelectorAll<HTMLElement>("[data-save-user]").forEach((button) => button.addEventListener("click", () => void saveUser(button.dataset.saveUser || "")));
  target.querySelectorAll<HTMLElement>("[data-credit-button]").forEach((button) => button.addEventListener("click", () => void addCredit(button.dataset.creditButton || "")));
  target.querySelectorAll<HTMLElement>("[data-detail-user]").forEach((button) => button.addEventListener("click", () => void showUserDetail(button.dataset.detailUser || "")));
  target.querySelectorAll<HTMLElement>("[data-reset-password-user]").forEach((button) => button.addEventListener("click", () => void resetUserPassword(button.dataset.resetPasswordUser || "", button.dataset.resetPasswordEmail || "")));
}

async function saveUser(userId: string): Promise<void> {
  const plan = $<HTMLSelectElement>(`.admin-user-plan[data-user-id="${CSS.escape(userId)}"]`);
  const status = $<HTMLSelectElement>(`.admin-user-status[data-user-id="${CSS.escape(userId)}"]`);
  await api(`/api/admin/users/${userId}/plan`, { method: "PATCH", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ plan_id: plan?.value }) });
  await api(`/api/admin/users/${userId}/status`, { method: "PATCH", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ status: status?.value }) });
  setStatus("Đã lưu thành viên", "ok");
  await renderUsers();
}

async function addCredit(userId: string): Promise<void> {
  const input = $<HTMLInputElement>(`[data-credit-input="${CSS.escape(userId)}"]`);
  const amount = Number(input?.value || 0);
  if (amount <= 0) return;
  await api(`/api/admin/users/${userId}/credits`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ amount, note: "Admin cộng credit" }) });
  setStatus("Đã cộng credit", "ok");
  await renderUsers();
}

async function resetUserPassword(userId: string, email: string): Promise<void> {
  if (!userId || !window.confirm(`Đặt lại mật khẩu cho ${email || "thành viên này"} về 12345678?`)) return;
  try {
    const result = await api<{ temporary_password: string; message: string }>(`/api/admin/users/${userId}/reset-password`, { method: "POST" });
    setStatus(`${result.message}. Mật khẩu tạm: ${result.temporary_password}`, "ok");
  } catch (error) {
    setStatus(error instanceof Error ? error.message : "Không đặt lại được mật khẩu", "error");
  }
}

async function showUserDetail(userId: string): Promise<void> {
  const detail = await api<{ user: AdminUser; affiliate: any | null; ledger: any[]; orders: any[]; withdrawals: any[] }>(`/api/admin/users/${userId}`);
  const target = $("#adminUserDetail");
  if (target) target.innerHTML = `<strong>${escapeHtml(detail.user.email)}</strong><span>Hôm nay: ${credit(detail.user.wallet?.daily)} / ${credit(detail.user.wallet?.daily_limit || 100)}</span><span>Credit mua: ${credit(detail.user.wallet?.purchased)}</span><span>Affiliate: ${escapeHtml(detail.affiliate?.code || "chưa có")}</span><span>Ledger: ${detail.ledger.length}</span><span>Đơn: ${detail.orders.length}</span><span>Rút tiền: ${detail.withdrawals.length}</span>`;
}

async function renderPackages(): Promise<void> {
  const data = await api<{ packages: any[] }>("/api/admin/credit-packages");
  const target = panel();
  if (!target) return;
  target.innerHTML = `<form id="adminPackageForm" class="admin-inline-form"><input class="control" name="id" placeholder="mã gói" required><input class="control" name="name" placeholder="Tên gói" required><input class="control" name="credits" type="number" placeholder="credit" required><input class="control" name="price_vnd" type="number" placeholder="giá VNĐ" required><input class="control" name="bonus_credits" type="number" placeholder="bonus"><label class="admin-check"><input name="active" type="checkbox" checked> Bật</label><button class="run-button" type="submit">Lưu gói</button></form><div class="admin-table-wrap"><table class="admin-table"><thead><tr><th>Gói</th><th>Credit</th><th>Giá</th><th>Số ảnh ước tính</th><th>Trạng thái</th><th></th></tr></thead><tbody>${data.packages.map((item) => `<tr><td>${escapeHtml(item.name)}${item.popular ? ` <span class="admin-badge">Phổ biến nhất</span>` : ""}<small>${escapeHtml(item.id)}</small></td><td>${credit(item.credits)} + ${credit(item.bonus_credits)}<small>Tổng ${credit(item.total_credits || item.credits)}</small></td><td>${money(item.price_vnd)}<small>${money(item.price_per_credit)}/credit</small></td><td>${packageEstimateSummary(item)}</td><td>${item.active ? "Bật" : "Tắt"}</td><td><button class="ghost-button" type="button" data-edit-package="${escapeHtml(item.id)}">Sửa</button></td></tr>`).join("")}</tbody></table></div>`;
  const form = $<HTMLFormElement>("#adminPackageForm");
  form?.addEventListener("submit", async (event) => { event.preventDefault(); const payload = formPayload(event.currentTarget as HTMLFormElement, ["credits", "price_vnd", "bonus_credits"]); await api("/api/admin/credit-packages", { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(payload) }); setStatus("Đã lưu gói credit", "ok"); await renderPackages(); });
  target.querySelectorAll<HTMLElement>("[data-edit-package]").forEach((button) => button.addEventListener("click", () => fillForm(form, data.packages.find((item) => item.id === button.dataset.editPackage))));
}

async function renderPlans(): Promise<void> {
  const data = await api<{ plans: any[] }>("/api/admin/plans");
  const target = panel();
  if (!target) return;
  target.innerHTML = `<form id="adminPlanForm" class="admin-plan-form"><input class="control" name="id" placeholder="mã gói" required><input class="control" name="name" placeholder="Tên gói" required><input class="control" name="level" type="number" min="0" placeholder="cấp độ" required><input class="control" name="monthly_price" type="number" min="0" placeholder="giá tháng VNĐ" required><input class="control" name="monthly_credits" type="number" min="0" placeholder="credit/tháng"><input class="control" name="priority_level" type="number" min="0" placeholder="ưu tiên"><input class="control" name="retention_days" type="number" min="1" placeholder="ngày lưu"><label class="admin-check"><input name="active" type="checkbox" checked> Bật</label><button class="run-button" type="submit">Lưu gói</button></form><div class="admin-table-wrap"><table class="admin-table"><thead><tr><th>Mã</th><th>Tên</th><th>Cấp</th><th>Giá tháng</th><th>Quyền lợi</th><th>Trạng thái</th><th></th></tr></thead><tbody>${data.plans.map((item) => `<tr><td>${escapeHtml(item.id)}</td><td>${escapeHtml(item.name)}</td><td>${item.level}</td><td>${money(item.monthly_price)}</td><td>${credit(item.monthly_credits)} credit/tháng<small>Ưu tiên ${credit(item.priority_level)} · lưu ${credit(item.retention_days)} ngày</small></td><td>${item.active ? "Bật" : "Tắt"}</td><td><button class="ghost-button" type="button" data-edit-plan="${escapeHtml(item.id)}">Sửa</button></td></tr>`).join("")}</tbody></table></div>`;
  const form = $<HTMLFormElement>("#adminPlanForm");
  form?.addEventListener("submit", async (event) => { event.preventDefault(); const payload = formPayload(event.currentTarget as HTMLFormElement, ["level", "monthly_price", "monthly_credits", "priority_level", "retention_days"]); await api("/api/admin/plans", { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(payload) }); setStatus("Đã lưu gói thành viên", "ok"); await renderPlans(); });
  target.querySelectorAll<HTMLElement>("[data-edit-plan]").forEach((button) => button.addEventListener("click", () => fillForm(form, data.plans.find((item) => item.id === button.dataset.editPlan))));
}

async function renderPricing(): Promise<void> {
  const data = await api<{ policy: any }>("/api/credit-policy");
  const costs = data.policy.costs;
  const target = panel();
  if (!target) return;
  target.innerHTML = `<div class="admin-pricing-grid"><div><h2>Tạo ảnh</h2>${costMatrixMarkup(costs.generate)}</div><div><h2>Chỉnh sửa ảnh</h2>${costMatrixMarkup(costs.edit)}</div><div><h2>Daily free</h2><p><strong>${credit(data.policy.daily_credit_amount)}</strong> credit/ngày</p><p>Reset lúc 0h theo giờ Việt Nam</p><p>Cảnh báo khi dưới ${credit(data.policy.low_credit_threshold)} credit</p></div></div>`;
}

function normalizedProviderId(value: unknown, fallback = "default"): string {
  return String(value || fallback).trim().toLowerCase().replace(/[^a-z0-9_-]+/g, "-").replace(/^-+|-+$/g, "") || fallback;
}

function normalizeProvider(provider: Partial<ApiProvider> = {}, index = 0): ApiProvider {
  const id = normalizedProviderId(provider.id, index === 0 ? "default" : `provider-${index + 1}`);
  return {
    id,
    name: String(provider.name || (id === "default" ? "Default" : `Provider ${index + 1}`)).trim() || id,
    base_url: String(provider.base_url || DEFAULT_API_BASE_URL).trim(),
    image_model: String(provider.image_model || DEFAULT_IMAGE_MODEL).trim() || DEFAULT_IMAGE_MODEL,
    api_mode: provider.api_mode === "responses" ? "responses" : "images",
    images_concurrency: Math.min(32, Math.max(1, Number.parseInt(String(provider.images_concurrency || 4), 10) || 4)),
    api_key_set: Boolean(provider.api_key_set || provider.api_key),
    api_key_masked: String(provider.api_key_masked || ""),
  };
}

function normalizeApiSettings(settings: Partial<ApiSettings> = {}): ApiSettings {
  const providers = (Array.isArray(settings.providers) && settings.providers.length ? settings.providers : [normalizeProvider()])
    .map((provider, index) => normalizeProvider(provider, index));
  const fallback = providers[0] || normalizeProvider();
  const active = providers.find((provider) => provider.id === settings.active_provider_id) || fallback;
  return { active_provider_id: active.id, codex_mode: settings.codex_mode === "responses" ? "responses" : "images", providers };
}

function activeAdminProvider(): ApiProvider {
  const settings = normalizeApiSettings(state.apiSettings || {});
  state.apiSettings = settings;
  return settings.providers.find((provider) => provider.id === settings.active_provider_id) || settings.providers[0] || normalizeProvider();
}

async function renderApiRouting(): Promise<void> {
  const [settingsData, routingData] = await Promise.all([
    api<{ settings: ApiSettings }>("/api/admin/api-settings"),
    api<{ routing: any }>("/api/admin/routing"),
  ]);
  state.apiSettings = normalizeApiSettings(settingsData.settings);
  renderApiRoutingPanel(routingData.routing.selected_source || "api");
}

function renderApiRoutingPanel(source: string): void {
  const settings = state.apiSettings;
  const provider = activeAdminProvider();
  const target = panel();
  if (!target || !settings) return;
  const providerOptions = settings.providers.map((item) => `<option value="${escapeHtml(item.id)}" ${item.id === settings.active_provider_id ? "selected" : ""}>${escapeHtml(item.name)} (${escapeHtml(item.id)})</option>`).join("");
  target.innerHTML = `<div class="admin-api-layout"><section class="admin-api-card"><h2>Routing tạo ảnh</h2><div class="admin-api-grid"><label class="field"><span>Nguồn xử lý mặc định</span><select id="adminRoutingSource" class="control"><option value="api" ${source === "api" ? "selected" : ""}>API / Gateway do admin cấu hình</option><option value="codex" ${source === "codex" ? "selected" : ""}>Codex local</option></select></label><label class="field"><span>Kênh Codex</span><select id="adminCodexMode" class="control"><option value="images" ${settings.codex_mode === "images" ? "selected" : ""}>Images</option><option value="responses" ${settings.codex_mode === "responses" ? "selected" : ""}>Responses</option></select></label><label class="field"><span>Provider API đang dùng</span><select id="adminProviderSelect" class="control">${providerOptions}</select></label></div><p class="admin-api-note">Người dùng chỉ tạo ảnh bằng credit. API key, gateway, model và routing chỉ nằm trong trang quản trị.</p></section><section class="admin-api-card"><div class="admin-api-card-heading"><h2>Provider API</h2><div><button id="adminNewProviderButton" class="ghost-button" type="button">Provider mới</button><button id="adminGatewayProviderButton" class="ghost-button" type="button">Gateway HVD AI</button></div></div><form id="adminApiProviderForm" class="admin-api-form"><label class="field"><span>Mã provider</span><input class="control" name="id" value="${escapeHtml(provider.id)}" required></label><label class="field"><span>Tên provider</span><input class="control" name="name" value="${escapeHtml(provider.name)}" required></label><label class="field"><span>Base URL</span><input class="control" name="base_url" type="url" value="${escapeHtml(provider.base_url)}" required></label><label class="field"><span>API key</span><input class="control" name="api_key" type="password" autocomplete="off" placeholder="${provider.api_key_set ? `Đã lưu ${escapeHtml(provider.api_key_masked || "")} - để trống để giữ nguyên` : "Nhập key mới"}"></label><label class="field"><span>Model ảnh</span><input class="control" name="image_model" value="${escapeHtml(provider.image_model)}" required></label><label class="field"><span>Luồng API</span><select class="control" name="api_mode"><option value="images" ${provider.api_mode === "images" ? "selected" : ""}>Images API</option><option value="responses" ${provider.api_mode === "responses" ? "selected" : ""}>Responses API</option></select></label><label class="field"><span>Concurrency</span><input class="control" name="images_concurrency" type="number" min="1" max="32" value="${provider.images_concurrency}" required></label><button class="run-button" type="submit">Lưu API & routing</button></form></section></div>`;
  $("#adminProviderSelect")?.addEventListener("change", (event) => {
    const selected = (event.currentTarget as HTMLSelectElement).value;
    state.apiSettings = normalizeApiSettings({ ...state.apiSettings, active_provider_id: selected });
    renderApiRoutingPanel(source);
  });
  $("#adminNewProviderButton")?.addEventListener("click", () => {
    const id = `provider-${Date.now()}`;
    const next = normalizeProvider({ id, name: "Provider mới" }, settings.providers.length);
    state.apiSettings = normalizeApiSettings({ ...settings, active_provider_id: next.id, providers: [...settings.providers, next] });
    renderApiRoutingPanel(source);
  });
  $("#adminGatewayProviderButton")?.addEventListener("click", () => {
    const id = `gateway-hvd-ai-${Date.now()}`;
    const next = normalizeProvider({ id, name: "Gateway HVD AI", base_url: DEFAULT_GATEWAY_BASE_URL, image_model: DEFAULT_IMAGE_MODEL, api_mode: "images" }, settings.providers.length);
    state.apiSettings = normalizeApiSettings({ ...settings, active_provider_id: next.id, providers: [...settings.providers, next] });
    renderApiRoutingPanel("api");
  });
  $("#adminApiProviderForm")?.addEventListener("submit", (event) => void saveApiRouting(event));
}

async function saveApiRouting(event: Event): Promise<void> {
  event.preventDefault();
  const form = new FormData(event.currentTarget as HTMLFormElement);
  const current = activeAdminProvider();
  const provider: any = {
    id: normalizedProviderId(form.get("id"), current.id),
    name: String(form.get("name") || current.name).trim(),
    base_url: String(form.get("base_url") || current.base_url).trim(),
    image_model: String(form.get("image_model") || current.image_model).trim(),
    api_mode: form.get("api_mode") === "responses" ? "responses" : "images",
    images_concurrency: Number(form.get("images_concurrency") || current.images_concurrency),
  };
  const apiKey = String(form.get("api_key") || "").trim();
  if (apiKey) provider.api_key = apiKey;
  const settings = normalizeApiSettings(state.apiSettings || {});
  const providers: ApiProvider[] = settings.providers.filter((item) => item.id !== current.id && item.id !== provider.id);
  const savedProvider = normalizeProvider({ ...current, ...provider }, providers.length);
  if (apiKey) savedProvider.api_key = apiKey;
  providers.push(savedProvider);
  const codexMode = ($("#adminCodexMode") as HTMLSelectElement | null)?.value === "responses" ? "responses" : "images";
  const source = ($("#adminRoutingSource") as HTMLSelectElement | null)?.value === "codex" ? "codex" : "api";
  const saved = await api<{ settings: ApiSettings }>("/api/admin/api-settings", {
    method: "PATCH",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ active_provider_id: provider.id, codex_mode: codexMode, providers }),
  });
  await api("/api/admin/routing", { method: "PATCH", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ source }) });
  state.apiSettings = normalizeApiSettings(saved.settings);
  setStatus("Đã lưu API & routing", "ok");
  await renderApiRouting();
}

function formPayload(form: HTMLFormElement, numericKeys: string[]): Record<string, unknown> {
  const payload: Record<string, unknown> = {};
  new FormData(form).forEach((value, key) => { payload[key] = numericKeys.includes(key) ? Number(value || 0) : String(value); });
  payload.active = (form.elements.namedItem("active") as HTMLInputElement | null)?.checked ?? true;
  return payload;
}

function fillForm(form: HTMLFormElement | null, item: any): void {
  if (!form || !item) return;
  Object.entries(item).forEach(([key, value]) => {
    const control = form.elements.namedItem(key) as HTMLInputElement | null;
    if (!control) return;
    if (control.type === "checkbox") control.checked = Boolean(value);
    else control.value = String(value ?? "");
  });
}

async function renderOrders(): Promise<void> {
  const data = await api<{ orders: any[] }>("/api/admin/orders");
  const target = panel();
  if (!target) return;
  target.innerHTML = `<div class="admin-table-wrap"><table class="admin-table"><thead><tr><th>Đơn</th><th>Thành viên</th><th>Số tiền</th><th>Credit</th><th>Trạng thái</th><th>Thao tác</th></tr></thead><tbody>${data.orders.map((item) => `<tr><td>${escapeHtml(item.order_code)}<small>${escapeHtml(item.provider_reference || "")}</small></td><td>${escapeHtml(item.user_email || item.user_id)}<small>${escapeHtml(item.user_phone || "")}</small></td><td>${money(item.amount_vnd)}</td><td>${item.credits} credit</td><td>${escapeHtml(item.status)}</td><td>${item.status === "pending" ? `<button class="run-button admin-approve-payment" type="button" data-approve-order="${escapeHtml(item.order_code)}" data-approve-email="${escapeHtml(item.user_email || item.user_id)}" data-approve-amount="${Number(item.amount_vnd || 0)}">Duyệt thanh toán</button>` : `<span class="admin-order-complete">Đã hoàn tất</span>`}</td></tr>`).join("")}</tbody></table></div>`;
  target.querySelectorAll<HTMLButtonElement>("[data-approve-order]").forEach((button) => button.addEventListener("click", () => void approvePaymentOrder(button)));
}

async function approvePaymentOrder(button: HTMLButtonElement): Promise<void> {
  const orderCode = button.dataset.approveOrder || "";
  const email = button.dataset.approveEmail || "khách hàng";
  const amount = Number(button.dataset.approveAmount || 0);
  if (!orderCode || !window.confirm(`Xác nhận đã nhận ${money(amount)} cho đơn ${orderCode} của ${email}? Credit sẽ được cộng ngay và không thể tự động hoàn tác.`)) return;
  const reference = window.prompt("Nhập mã giao dịch ngân hàng để lưu đối soát:", "")?.trim() || "";
  if (reference.length < 3) {
    setStatus("Cần nhập mã giao dịch ngân hàng để duyệt đơn", "error");
    return;
  }
  button.disabled = true;
  try {
    const result = await api<{ message: string }>(`/api/admin/orders/${encodeURIComponent(orderCode)}/approve`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ transaction_reference: reference }),
    });
    setStatus(result.message, "ok");
    await loadAdminTab("orders");
  } catch (error) {
    setStatus(error instanceof Error ? error.message : "Không duyệt được thanh toán", "error");
    button.disabled = false;
  }
}

async function renderAffiliates(): Promise<void> {
  const data = await api<{ affiliates: any[]; settings: any; commissions: any[] }>("/api/admin/affiliates");
  const target = panel();
  if (!target) return;
  const settings = data.settings || { commission_rates: [30, 10, 10, 10], signup_bonus_credits: 100, min_plan_level: 1, min_withdrawal_vnd: 100000 };
  target.innerHTML = `
    <form id="adminAffiliateSettingsForm" class="admin-inline-form">
      <label class="field"><span>Lịch hoa hồng</span><input class="control" name="commission_rates" value="${escapeHtml((settings.commission_rates || []).join(","))}" placeholder="30,10,10,10"></label>
      <label class="field"><span>Bonus nhập mã</span><input class="control" name="signup_bonus_credits" type="number" min="0" value="${Number(settings.signup_bonus_credits || 0)}"></label>
      <label class="field"><span>Cấp gói tối thiểu</span><input class="control" name="min_plan_level" type="number" min="0" value="${Number(settings.min_plan_level || 0)}"></label>
      <label class="field"><span>Rút tối thiểu</span><input class="control" name="min_withdrawal_vnd" type="number" min="100000" value="${Number(settings.min_withdrawal_vnd || 100000)}"></label>
      <button class="run-button" type="submit">Lưu cấu hình</button>
    </form>
    <div class="admin-table-wrap">
      <table class="admin-table">
        <thead><tr><th>Thành viên affiliate</th><th>Mã</th><th>Gói</th><th>Hiệu quả</th><th>Hoa hồng</th><th>Cấu hình riêng</th><th>Trạng thái</th><th></th></tr></thead>
        <tbody>${data.affiliates.map((item) => `<tr>
          <td>${escapeHtml(item.email)}<small>${escapeHtml(item.phone)}</small></td>
          <td>${escapeHtml(item.code)}</td>
          <td>${escapeHtml(item.plan_id)}</td>
          <td>${credit(item.referred_count)} đăng ký<small>${credit(item.paid_referred_count)} đã nạp · ${money(item.referred_revenue_vnd)}</small></td>
          <td>${money(item.available_balance)}<small>Tổng ${money(item.total_commission_vnd)}</small></td>
          <td><input class="control affiliate-rates-input" data-affiliate-rates="${escapeHtml(item.user_id)}" value="${escapeHtml((item.commission_rates || settings.commission_rates || []).join(","))}" placeholder="30,10,10,10"></td>
          <td><select class="control affiliate-status-input" data-affiliate-status="${escapeHtml(item.user_id)}"><option value="active" ${item.status === "active" ? "selected" : ""}>active</option><option value="paused" ${item.status === "paused" ? "selected" : ""}>paused</option><option value="banned" ${item.status === "banned" ? "selected" : ""}>banned</option></select></td>
          <td><button class="ghost-button" type="button" data-save-affiliate="${escapeHtml(item.user_id)}">Lưu</button></td>
        </tr>`).join("")}</tbody>
      </table>
    </div>
    <div class="admin-table-wrap">
      <table class="admin-table">
        <thead><tr><th>Affiliate</th><th>User được giới thiệu</th><th>Đơn</th><th>Lần</th><th>Hoa hồng</th><th>Trạng thái</th></tr></thead>
        <tbody>${data.commissions.slice(0, 200).map((item) => `<tr><td>${escapeHtml(item.code)}<small>${escapeHtml(shortUserId(item.affiliate_user_id))}</small></td><td>${escapeHtml(shortUserId(item.referred_user_id))}</td><td>${escapeHtml(item.order_code)}<small>${money(item.order_amount_vnd)}</small></td><td>${credit(item.commission_index)}</td><td>${money(item.amount_vnd)}<small>${credit(item.rate_percent)}%</small></td><td>${escapeHtml(item.status)}</td></tr>`).join("")}</tbody>
      </table>
    </div>
  `;
  $("#adminAffiliateSettingsForm")?.addEventListener("submit", async (event) => {
    event.preventDefault();
    const form = new FormData(event.currentTarget as HTMLFormElement);
    await api("/api/admin/affiliate-settings", {
      method: "PATCH",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        commission_rates: String(form.get("commission_rates") || ""),
        signup_bonus_credits: Number(form.get("signup_bonus_credits") || 0),
        min_plan_level: Number(form.get("min_plan_level") || 0),
        min_withdrawal_vnd: Number(form.get("min_withdrawal_vnd") || 100000),
      }),
    });
    setStatus("Đã lưu cấu hình affiliate", "ok");
    await renderAffiliates();
  });
  target.querySelectorAll<HTMLElement>("[data-save-affiliate]").forEach((button) => button.addEventListener("click", () => void saveAffiliate(button.dataset.saveAffiliate || "")));
}

async function saveAffiliate(userId: string): Promise<void> {
  const status = $<HTMLSelectElement>(`[data-affiliate-status="${CSS.escape(userId)}"]`);
  const rates = $<HTMLInputElement>(`[data-affiliate-rates="${CSS.escape(userId)}"]`);
  await api(`/api/admin/affiliates/${userId}`, {
    method: "PATCH",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ status: status?.value, commission_rates: rates?.value || "" }),
  });
  setStatus("Đã lưu affiliate", "ok");
  await renderAffiliates();
}

function shortUserId(value: unknown): string {
  const text = String(value || "");
  return text.length > 12 ? `${text.slice(0, 8)}...${text.slice(-4)}` : text;
}

async function renderWithdrawals(): Promise<void> {
  const data = await api<{ withdrawals: any[] }>("/api/admin/withdrawals");
  const target = panel();
  if (!target) return;
  target.innerHTML = `<div class="admin-table-wrap"><table class="admin-table"><thead><tr><th>Thành viên</th><th>Số tiền</th><th>Ngân hàng</th><th>Trạng thái</th><th>Ghi chú</th></tr></thead><tbody>${data.withdrawals.map((item) => `<tr><td>${escapeHtml(item.user_email || item.user_id)}<small>${escapeHtml(item.user_phone || "")}</small></td><td>${money(item.amount)}</td><td>${escapeHtml(item.bank_name)}<small>${escapeHtml(item.account_number)} · ${escapeHtml(item.account_holder)}</small></td><td><select class="control withdrawal-status" data-withdrawal-id="${escapeHtml(item.id)}"><option ${item.status === "requested" ? "selected" : ""}>requested</option><option ${item.status === "under_review" ? "selected" : ""}>under_review</option><option ${item.status === "approved" ? "selected" : ""}>approved</option><option ${item.status === "paid" ? "selected" : ""}>paid</option><option ${item.status === "rejected" ? "selected" : ""}>rejected</option></select></td><td><input class="control withdrawal-note" data-withdrawal-note="${escapeHtml(item.id)}" value="${escapeHtml(item.note || "")}" placeholder="Ghi chú duyệt"></td></tr>`).join("")}</tbody></table></div>`;
  target.querySelectorAll<HTMLSelectElement>(".withdrawal-status").forEach((select) => select.addEventListener("change", async () => { const note = $<HTMLInputElement>(`[data-withdrawal-note="${CSS.escape(select.dataset.withdrawalId || "")}"]`); await api(`/api/admin/withdrawals/${select.dataset.withdrawalId}`, { method: "PATCH", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ status: select.value, note: note?.value || "" }) }); setStatus("Đã cập nhật rút tiền", "ok"); }));
}

async function renderIntegrations(): Promise<void> {
  const data = await api<{ integrations: Record<string, boolean> }>("/api/admin/integrations");
  const fields: Array<[string, string, "password" | "text"]> = [
    ["telegram_bot_token", "Telegram bot token", "password"],
    ["telegram_admin_id", "Telegram admin ID", "text"],
    ["telegram_group_id", "Telegram group ID", "text"],
    ["sepay_token", "SePay token", "password"],
    ["sepay_webhook_secret", "SePay webhook secret", "password"],
    ["sepay_qr_template", "SePay QR template", "text"],
  ];
  const target = panel();
  if (!target) return;
  target.innerHTML = `<form id="adminIntegrationForm" class="admin-integration-form">${fields.map(([key, label, type]) => `<label class="field"><span>${escapeHtml(label)} ${data.integrations[key] ? "(đã cấu hình)" : "(chưa cấu hình)"}</span><input class="control" name="${key}" type="${type}" autocomplete="off" inputmode="${key.endsWith("_id") ? "numeric" : "text"}" placeholder="Để trống nếu giữ nguyên"></label>`).join("")}<div class="admin-row-actions"><button class="run-button" type="submit">Lưu tích hợp</button><button id="adminTelegramTestButton" class="ghost-button" type="button">Kiểm tra Telegram</button></div></form>`;
  $("#adminIntegrationForm")?.addEventListener("submit", async (event) => {
    event.preventDefault();
    const form = new FormData(event.currentTarget as HTMLFormElement);
    const payload: Record<string, string> = {};
    form.forEach((value, key) => { if (String(value).trim()) payload[key] = String(value).trim(); });
    await api("/api/admin/integrations", { method: "PATCH", headers: { "Content-Type": "application/json" }, body: JSON.stringify(payload) });
    setStatus("Đã lưu tích hợp", "ok");
    await renderIntegrations();
  });
  $("#adminTelegramTestButton")?.addEventListener("click", async () => {
    try {
      const result = await api<{ target_type?: string }>("/api/admin/integrations/telegram/test", { method: "POST" });
      setStatus(`Telegram gửi thử thành công (${result.target_type === "group" ? "nhóm" : "admin"})`, "ok");
    } catch (error) {
      setStatus(error instanceof Error ? error.message : "Không kiểm tra được Telegram", "error");
    }
  });
}

async function renderAudit(): Promise<void> {
  const data = await api<{ logs: any[] }>("/api/admin/audit-logs");
  const target = panel();
  if (target) target.innerHTML = `<div class="admin-table-wrap"><table class="admin-table"><thead><tr><th>Thời gian</th><th>Admin</th><th>Hành động</th><th>Mục tiêu</th><th>Chi tiết</th></tr></thead><tbody>${data.logs.map((item) => `<tr><td>${escapeHtml(new Date(item.created_at).toLocaleString("vi-VN"))}</td><td>${escapeHtml(item.actor_email || item.actor_user_id || "system")}</td><td>${escapeHtml(item.action)}</td><td>${escapeHtml(item.target_id || "")}</td><td>${escapeHtml(item.details || "")}</td></tr>`).join("")}</tbody></table></div>`;
}

function init(): void {
  $("#adminLoginForm")?.addEventListener("submit", (event) => void login(event));
  $("#adminLogoutButton")?.addEventListener("click", () => void logout());
  document.querySelectorAll<HTMLElement>(".admin-tab").forEach((button) => button.addEventListener("click", () => void loadAdminTab(button.dataset.adminTab || "users")));
  void refreshSession();
}

init();
